import { Check, ChevronsUpDown } from 'lucide-react';
import { useMemo, useState, type ButtonHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@nucleus-commerce/ui/components/command';
import { Popover, PopoverContent, PopoverTrigger } from '@nucleus-commerce/ui/components/popover';
import { cn } from '@nucleus-commerce/ui/lib/utils';

/** IANA zones the browser knows; a stored zone it does not know is kept at the top. */
function zones(current: string): string[] {
  let list: string[];
  try {
    list = Intl.supportedValuesOf('timeZone');
  } catch {
    list = ['UTC'];
  }
  return current && !list.includes(current) ? [current, ...list] : list;
}

export interface TimezoneComboboxProps extends Pick<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'aria-describedby' | 'aria-invalid'
> {
  /** Set by `FormControl` so the field's label names the button. */
  id?: string;
  /** Empty follows the store. */
  value: string;
  onChange: (value: string) => void;
  storeTimezone: string;
  disabled?: boolean;
}

export function TimezoneCombobox({
  id,
  value,
  onChange,
  storeTimezone,
  disabled,
  ...aria
}: TimezoneComboboxProps) {
  const { t } = useTranslation('chatSettings');
  const [open, setOpen] = useState(false);
  const options = useMemo(() => zones(value), [value]);
  const storeLabel = t('hours.storeTimezone', { timezone: storeTimezone });

  const pick = (next: string) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          {...aria}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal sm:w-80"
        >
          <span className="truncate">{value || storeLabel}</span>
          <ChevronsUpDown className="opacity-50" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
        <Command>
          <CommandInput placeholder={t('hours.timezoneSearch')} />
          <CommandList>
            <CommandEmpty>{t('hours.timezoneEmpty')}</CommandEmpty>
            <CommandGroup>
              <CommandItem value={`store ${storeTimezone}`} onSelect={() => pick('')}>
                <Check className={cn(value === '' ? 'opacity-100' : 'opacity-0')} aria-hidden />
                {storeLabel}
              </CommandItem>
              {options.map((zone) => (
                <CommandItem key={zone} value={zone} onSelect={() => pick(zone)}>
                  <Check className={cn(value === zone ? 'opacity-100' : 'opacity-0')} aria-hidden />
                  {zone}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
